import React, { useEffect, useMemo, useState } from 'react';

// Generate a readable alphanumeric code: 3 letters + 3 digits (e.g. FAM142)
const generateCode = () => {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // skip I/O for readability
  const digits = '23456789';                    // skip 0/1 for readability
  let out = '';
  for (let i = 0; i < 3; i++) out += letters.charAt(Math.floor(Math.random() * letters.length));
  for (let i = 0; i < 3; i++) out += digits.charAt(Math.floor(Math.random() * digits.length));
  return out;
};

const DiscountCodeModal = ({
  open,
  title = 'Generate discount code',
  defaultAmount = '',
  existingCodes = [],
  onClose,
  onSubmit
}) => {
  const [code, setCode] = useState('');
  const [amountOff, setAmountOff] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset on open
  useEffect(() => {
    if (open) {
      setCode(generateCode());
      setAmountOff(defaultAmount ? String(defaultAmount) : '');
      setExpiresAt('');
      setMaxRedemptions('');
      setNotes('');
      setError('');
    }
  }, [open, defaultAmount]);

  const existingUpper = useMemo(
    () => new Set((existingCodes || []).map(c => String(c.code || '').toUpperCase())),
    [existingCodes]
  );

  if (!open) return null;

  const handleSubmit = async () => {
    setError('');
    const upper = String(code).trim().toUpperCase();
    if (!/^[A-Z0-9]{3,20}$/.test(upper)) {
      setError('Code must be 3–20 alphanumeric characters.');
      return;
    }
    const amount = parseFloat(amountOff);
    if (!amount || amount <= 0) {
      setError('Amount off must be greater than 0.');
      return;
    }
    if (existingUpper.has(upper)) {
      setError(`Code "${upper}" already exists in this studio.`);
      return;
    }
    const max = maxRedemptions ? parseInt(maxRedemptions, 10) : null;
    if (maxRedemptions && (!max || max < 1)) {
      setError('Max redemptions must be a positive whole number.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        code: upper,
        amountOff: amount,
        expiresAt: expiresAt || null,
        maxRedemptions: max,
        notes: notes.trim()
      });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save code');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="Enter code (e.g. FAMILY20)"
                maxLength={20}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setCode(generateCode())}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Regenerate
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">3–20 alphanumeric characters. Students will type this exactly.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount off ($)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={amountOff}
                onChange={e => setAmountOff(e.target.value)}
                placeholder="Enter amount"
                min="0.01"
                step="1"
                className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires (optional)</label>
              <input
                type="date"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max redemptions (optional)</label>
              <input
                type="number"
                value={maxRedemptions}
                onChange={e => setMaxRedemptions(e.target.value)}
                placeholder="Unlimited"
                min="1"
                step="1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal label, e.g. Smith family"
              maxLength={500}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Only visible to you.</p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300"
          >
            {submitting ? 'Saving...' : 'Generate code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscountCodeModal;
